import { useState, useEffect } from 'react';

const getLocalStorageValue = <T>(key: string, defaultValue: T): T => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.warn(`Failed to read localStorage key "${key}"`, error);
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore cleanup failures and fall back to default value.
    }
  }

  return defaultValue;
};

/*
 * Custom hook that keeps local state in a component and local storage synced.
 * This is initialized as follows: const [state, setState] = useLocalStorage(keyInLocalStorage, defaultValue) and can therafter be used as a regular internal state in any component.
 */
export const useLocalStorage = <T>(key: string, defaultValue: T) => {
  const [value, set] = useState<T>(() =>
    getLocalStorageValue(key, defaultValue),
  );

  const setValue = (value: T) => {
    set(value);
  };

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn(`Failed to write localStorage key "${key}"`, error);
    }
  }, [key, value]);

  return [value, setValue] as [T, (value: T) => void];
};
